import { useEffect, useState, useCallback } from 'react';
import adminService from '../services/adminService';
import { useBranding } from '../contexts/BrandingContext';

/**
 * Feature-flag state — read by every surface that needs to gate UI on
 * a platform admin's flag toggle.
 *
 * Source-of-truth precedence (highest first):
 *   1. localStorage         — fastest, in-tab feedback after a toggle
 *   2. branding API         — global state shared across ALL users
 *   3. server flag list     — defaults from the canonical flags endpoint
 *
 * The branding-API layer is what makes a disable propagate to every
 * other user / browser: when the admin saves a toggle, it's stored as
 * a JSON-encoded ``feature_flag_overrides`` row in branding_config,
 * and BrandingContext picks it up on every fresh page load.
 */

const LS_KEY = 'wms.featureFlagOverrides.v1';
const BRANDING_FLAGS_KEY = 'feature_flag_overrides';

const readLocal = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeLocal = (overrides) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(overrides)); }
  catch { /* quota / disabled storage — silent */ }
};

const readBrandingFlags = (branding) => {
  const raw = branding?.[BRANDING_FLAGS_KEY];
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch { return {}; }
};

/** Persist a single flag override. Used by the FeatureFlags page on
 *  toggle for instant in-tab feedback. */
export function setFeatureFlagOverride(flagKey, value) {
  if (!flagKey) return readLocal();
  const next = { ...readLocal(), [flagKey]: { ...(readLocal()[flagKey] || {}), ...value } };
  writeLocal(next);
  window.dispatchEvent(new Event('wms-feature-flags-changed'));
  return next;
}

export function clearFeatureFlagOverrides() {
  writeLocal({});
  window.dispatchEvent(new Event('wms-feature-flags-changed'));
}

/**
 * Returns ``{ flags, isEnabled, refetch }``.
 *
 * - ``flags`` — array of flag objects (server data + branding + localStorage overlay).
 * - ``isEnabled(key, fallback = true)`` — convenience for gating UI.
 * - ``refetch`` — re-pull the canonical flag list from the server.
 */
export default function useFeatureFlags() {
  const branding = useBranding();
  const [serverFlags, setServerFlags] = useState([]);
  const [localOverrides, setLocalOverrides] = useState(() => readLocal());

  const refetch = useCallback(async () => {
    try {
      const { data } = await adminService.getFeatureFlags();
      const list = Array.isArray(data) ? data : data?.results || data?.flags || [];
      setServerFlags(list);
    } catch {
      setServerFlags([]);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  // Re-read localStorage when any tab updates it (own tab via custom
  // event, other tabs via the native storage event).
  useEffect(() => {
    const sync = () => setLocalOverrides(readLocal());
    window.addEventListener('storage', sync);
    window.addEventListener('wms-feature-flags-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('wms-feature-flags-changed', sync);
    };
  }, []);

  const brandingOverrides = readBrandingFlags(branding);

  // Merged flag list — server data + branding API overlay + local overlay.
  const merged = serverFlags.map((f) => {
    const fromBranding = brandingOverrides[f.key];
    const fromLocal = localOverrides[f.key];
    return {
      ...f,
      ...(fromBranding !== undefined ? { is_enabled: !!fromBranding } : {}),
      ...(fromLocal || {}),
    };
  });
  // Surface override-only flags so callers can gate even when server
  // hasn't returned a row for them yet.
  Object.keys(brandingOverrides).forEach((key) => {
    if (!merged.find((f) => f.key === key)) {
      merged.push({ key, is_enabled: !!brandingOverrides[key] });
    }
  });
  Object.keys(localOverrides).forEach((key) => {
    if (!merged.find((f) => f.key === key)) {
      merged.push({ key, ...localOverrides[key] });
    }
  });

  const isEnabled = useCallback((key, fallback = true) => {
    if (!key) return fallback;
    // Local wins (in-tab optimistic update).
    if (localOverrides[key]?.is_enabled !== undefined) return Boolean(localOverrides[key].is_enabled);
    // Then branding (global, persists across users).
    if (brandingOverrides[key] !== undefined) return Boolean(brandingOverrides[key]);
    // Then server defaults.
    const f = serverFlags.find((x) => x.key === key);
    if (f) return Boolean(f.is_enabled);
    return fallback;
  }, [localOverrides, brandingOverrides, serverFlags]);

  return { flags: merged, isEnabled, refetch };
}
